import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';

type CeremonialLoaderProps = {
  open: boolean;
  title: string;
  kicker?: string;
  messages: string[];
  /** Ms entre as trocas das mensagens divertidas. */
  intervalMs?: number;
};

/**
 * Overlay full-screen com spinner cerimonial dourado + título fixo
 * + mensagens rotativas divertidas. Usado em momentos longos do fluxo
 * (moderação AI, mint na blockchain).
 *
 * - Bloqueia interação por trás (z-50)
 * - Respeita prefers-reduced-motion (sem rotação do spinner, sem fade)
 * - `inert` no body — não está aqui pra ser fechado pelo usuário
 */
export function CeremonialLoader({
  open,
  title,
  kicker,
  messages,
  intervalMs = 1800,
}: CeremonialLoaderProps) {
  const reduced = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);

  // Embaralha a ordem das mensagens pra cada aparição (sensação de "viva")
  const orderedMessages = useMemo(() => {
    if (!open) return messages;
    const arr = [...messages];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || orderedMessages.length <= 1) return;
    setIndex(0);
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % orderedMessages.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [open, orderedMessages, intervalMs]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="ceremonial-loader"
          role="status"
          aria-live="polite"
          aria-busy="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-night/90 px-6 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduced ? 0 : 0.4 }}
        >
          {/* Ambient glow */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.18) 0%, transparent 65%)',
            }}
          />

          <div className="relative z-10 flex max-w-xl flex-col items-center text-center">
            {/* Spinner cerimonial */}
            <div className="relative h-28 w-28">
              <svg
                viewBox="0 0 100 100"
                className={reduced ? '' : 'h-full w-full animate-spin'}
                style={reduced ? undefined : { animationDuration: '2.4s' }}
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="loader-gold" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#E8D5A8" />
                    <stop offset="0.5" stopColor="#D4AF37" />
                    <stop offset="1" stopColor="#8B6F2C" />
                  </linearGradient>
                </defs>
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="rgba(212,175,55,0.15)"
                  strokeWidth="3"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="url(#loader-gold)"
                  strokeWidth="3"
                  strokeDasharray="80 184"
                  strokeLinecap="round"
                />
              </svg>

              {/* Núcleo dourado pulsante */}
              <div
                aria-hidden="true"
                className="absolute inset-0 flex items-center justify-center"
              >
                <span
                  className={
                    reduced
                      ? 'inline-block h-3 w-3 rounded-full bg-gold-gradient'
                      : 'inline-block h-3 w-3 rounded-full bg-gold-gradient animate-glow-pulse'
                  }
                />
              </div>
            </div>

            {kicker ? (
              <p className="mt-8 font-mono text-xs uppercase tracking-stamp text-gold">
                {kicker}
              </p>
            ) : null}

            <h2 className="mt-3 max-w-md font-display text-2xl leading-snug text-parchment md:text-3xl">
              {title}
            </h2>

            {/* Mensagem rotativa */}
            <div className="mt-8 h-12 w-full max-w-md overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.p
                  key={orderedMessages[index] ?? 'message'}
                  initial={reduced ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduced ? undefined : { opacity: 0, y: -8 }}
                  transition={{ duration: reduced ? 0 : 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="font-display text-base italic leading-snug text-champagne md:text-lg"
                >
                  {orderedMessages[index] ?? ''}
                </motion.p>
              </AnimatePresence>
            </div>

            <p className="mt-6 font-mono text-[10px] uppercase tracking-stamp text-champagne/40">
              Modo demonstração · processo simulado
            </p>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
